import z from "zod";
import CharityService from "../../../../services/CharityService";
import { mapErrorsToFields } from "../../../../utils";

const api = new CharityService();
export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    registrationNo?: string[];
    income?: string[];
    expenditure?: string[];
    website?: string[];
    aimsAndActivities?: string[];
    whatItDoes?: string[];
    whoItHelps?: string[];
    howItWorks?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    id?:number;
    name?: string;
    registrationNo?: string;
    income?: number;
    expenditure?: number;
    website?: string;
    aimsAndActivities?: string;
    whatItDoes?: string;
    whoItHelps?: string;
    howItWorks?: string;
  };
};

const FormSchema = z.object({
  name: z.string().min(1, { message: "Name is required." }),
  registrationNo: z.string().optional(),
  income: z.number().optional(),
  expenditure: z.number().optional(),
  website: z.string().optional(),
  aimsAndActivities: z.string().optional(),
  whatItDoes: z.string().optional(),
  whoItHelps: z.string().optional(),
  howItWorks: z.string().optional(),
});

export async function createCharity(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
    registrationNo: formData.get("registrationNo")?.toString() || "",
    income: parseFloat(formData.get("income")?.toString() || "0"),
    expenditure: parseFloat(formData.get("expenditure")?.toString() || "0"),
    website: formData.get("website")?.toString() || "",
    aimsAndActivities: formData.get("aimsAndActivities")?.toString() || "",
    whatItDoes: formData.get("whatItDoes")?.toString() || "",
    whoItHelps: formData.get("whoItHelps")?.toString() || "",
    howItWorks: formData.get("howItWorks")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create charity.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.createCharity(validatedFields.data);
    if (result.status === 201)
      return {
        errors: {},
        message: "Charity created successfully.",
        defaultValues:{
          id:result.data.id,
          ...defaultValues
        },
        success: true,
      };
    else if (result.status === 400)
      return {
        errors: {},
        message: result.data.description,
        defaultValues,
        success: false,
      };
  } catch (error) {
    console.error("Error creating charity:", error);
  }
  return {
    errors: {},
    message: "Failed to create charity.",
    defaultValues,
    success: false,
  };
}

export async function updateCharity(
  id: number,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
    registrationNo: formData.get("registrationNo")?.toString() || "",
    income: parseFloat(formData.get("income")?.toString() || "0"),
    expenditure: parseFloat(formData.get("expenditure")?.toString() || "0"),
    website: formData.get("website")?.toString() || "",
    aimsAndActivities: formData.get("aimsAndActivities")?.toString() || "",
    whatItDoes: formData.get("whatItDoes")?.toString() || "",
    whoItHelps: formData.get("whoItHelps")?.toString() || "",
    howItWorks: formData.get("howItWorks")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update charity.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.updateCharity({
      id,
      ...validatedFields.data,
    });
    if (result.status === 204)
      return {
        errors: {},
        message: "Charity updated successfully.",
        defaultValues,
        success: true,
      };
  } catch (error) {
    console.error("Error updating charity:", error);
  }
  return {
    errors: {},
    message: "Failed to update charity.",
    defaultValues,
    success: false,
  };
}

export async function deleteCharity(
  id: number
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteCharity(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Database Error:", error);
  }
  return { success: false };
}
